import { z } from 'zod';
import type { Domain, Location, PrinterDevice, Restaurant } from './types';

/**
 * Platform admin console contract (PLAN.md §21).
 *
 * Access is limited to rows in `private.platform_admins`. Every endpoint under
 * /api/platform/admin/* is checked server-side against that table; the frontend
 * guard is only cosmetic. All mutations are written to public.audit_log with an
 * `admin.` action prefix, and opening a restaurant's support view is audited too.
 */

export const ADMIN_API = {
  me: '/api/platform/admin/me',
  overview: '/api/platform/admin/overview',
  restaurants: '/api/platform/admin/restaurants',
  restaurant: (id: string) => `/api/platform/admin/restaurants/${id}`,
  restaurantStatus: (id: string) => `/api/platform/admin/restaurants/${id}/status`,
  restaurantPlan: (id: string) => `/api/platform/admin/restaurants/${id}/plan`,
  tables: '/api/platform/admin/tables',
  table: (name: string) => `/api/platform/admin/tables/${encodeURIComponent(name)}`,
  audit: '/api/platform/admin/audit',
  admins: '/api/platform/admin/admins',
  admin: (userId: string) => `/api/platform/admin/admins/${userId}`,
} as const;

/** Read-only tables the generic data browser may expose. */
export const ADMIN_TABLES = [
  'restaurants',
  'locations',
  'memberships',
  'profiles',
  'orders',
  'order_items',
  'reservations',
  'reservation_settings',
  'opening_hours',
  'special_hours',
  'menu_categories',
  'menu_items',
  'modifier_groups',
  'modifier_options',
  'menu_item_modifier_groups',
  'printer_devices',
  'print_jobs',
  'location_tables',
  'domains',
  'audit_log',
] as const;

export type AdminTableName = (typeof ADMIN_TABLES)[number];

export interface AdminMeResponse {
  is_admin: boolean;
}

export interface AdminTotals {
  restaurants: number;
  suspended_restaurants: number;
  locations: number;
  published_locations: number;
  orders_30d: number;
  revenue_30d_cents: number;
  reservations_30d: number;
  active_printers: number;
  printers_online: number;
}

export interface AdminAuditRow {
  id: string;
  restaurant_id: string;
  restaurant_name: string;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  target: string | null;
  meta: Record<string, unknown> | null;
  created_at: string;
}

export interface AdminOrderSummary {
  id: string;
  order_number: number;
  restaurant_name: string;
  location_name: string;
  status: string;
  payment_method: string;
  payment_status: string;
  total_cents: number;
  created_at: string;
}

export interface AdminOverviewResponse {
  totals: AdminTotals;
  recent_audit: AdminAuditRow[];
  recent_orders: AdminOrderSummary[];
}

export interface AdminRestaurantRow {
  id: string;
  name: string;
  status: Restaurant['status'];
  plan: string;
  stripe_charges_enabled: boolean;
  created_at: string;
  location_count: number;
  published_location_count: number;
  member_count: number;
  orders_30d: number;
  revenue_30d_cents: number;
  last_order_at: string | null;
}

export interface AdminRestaurantsResponse {
  restaurants: AdminRestaurantRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminMemberRow {
  user_id: string;
  email: string | null;
  full_name: string | null;
  role: string;
  location_ids: string[] | null;
  created_at: string;
}

export interface AdminReservationSummary {
  id: string;
  location_id: string;
  status: string;
  party_size: number;
  requested_for: string;
  created_at: string;
}

export interface AdminRestaurantDetailResponse {
  restaurant: Restaurant;
  locations: Location[];
  members: AdminMemberRow[];
  devices: PrinterDevice[];
  domains: Domain[];
  recent_orders: AdminOrderSummary[];
  recent_reservations: AdminReservationSummary[];
}

export interface AdminTableColumn {
  name: string;
  type: string;
}

export interface AdminTableSummary {
  name: string;
  columns: AdminTableColumn[];
  approx_rows: number;
}

export interface AdminTablesResponse {
  tables: AdminTableSummary[];
}

export type AdminCell = string | number | boolean | null;

export interface AdminTablePageResponse {
  table: string;
  columns: string[];
  rows: AdminCell[][];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminAuditResponse {
  entries: AdminAuditRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminAdminsResponse {
  admins: AdminMemberRow[];
}

export interface AdminMutationResponse {
  ok: true;
}

export interface AdminRestaurantStatusResponse {
  ok: true;
  status: Restaurant['status'];
}

export interface AdminSetPlanResponse {
  ok: true;
  plan: string;
}

export const adminPageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(100000).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(25),
});

export const adminRestaurantsQuerySchema = adminPageQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
});

export const adminTableQuerySchema = adminPageQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  sort: z.string().trim().max(64).optional(),
  desc: z
    .union([z.literal('true'), z.literal('false'), z.literal('1'), z.literal('0')])
    .optional()
    .transform((value) => value !== 'false' && value !== '0'),
});

export const adminAuditQuerySchema = adminPageQuerySchema.extend({
  restaurant_id: z.string().uuid().optional(),
  action: z.string().trim().max(120).optional(),
});

export const adminSetStatusSchema = z
  .object({
    status: z.enum(['active', 'suspended']),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export const adminAddAdminSchema = z
  .object({
    email: z.string().trim().toLowerCase().email().max(254),
  })
  .strict();

export const adminSetPlanSchema = z
  .object({
    plan: z.enum(['free', 'starter', 'growth', 'pro']),
  })
  .strict();

export type AdminRestaurantsQuery = z.infer<typeof adminRestaurantsQuerySchema>;
export type AdminTableQuery = z.infer<typeof adminTableQuerySchema>;
export type AdminAuditQuery = z.infer<typeof adminAuditQuerySchema>;
export type AdminSetStatusRequest = z.infer<typeof adminSetStatusSchema>;
export type AdminAddAdminRequest = z.infer<typeof adminAddAdminSchema>;
export type AdminSetPlanRequest = z.infer<typeof adminSetPlanSchema>;
